var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { DataTable, FlexBox, IconButton, Text, Tooltip, } from '@dixa/lib-shared-habla';
import { useEffect, useMemo } from 'react';
import { facebookMessengerApi, } from 'apis/facebookMessenger';
import { searchApi } from 'apis/search';
import { appActions } from 'app/app.actions';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { FacebookAuthenticator } from './components/facebookAuthenticator/facebookAuthenticator';
import { FacebookEditPageModal } from './components/facebookEditPageModal/facebookEditPageModal';
/** Represents the facebook tab. */
export const Facebook = () => {
    const dispatch = useAppDispatch();
    const notify = useNotifications();
    const [deleteFacebookPage] = facebookMessengerApi.useDeleteFacebookPageMutation();
    const { facebookApps, isLoadingFacebook } = facebookMessengerApi.useReadApplicationsQuery(undefined, {
        selectFromResult: (_a) => {
            var { data, isLoading } = _a, rest = __rest(_a, ["data", "isLoading"]);
            return (Object.assign(Object.assign({}, rest), { facebookApps: (data === null || data === void 0 ? void 0 : data.data.applications) || [], isLoadingFacebook: isLoading }));
        },
    });
    const [readSearchQueue, { searchQueueHits, isLoadingQueues }] = searchApi.useLazyReadSearchQueueQuery({
        selectFromResult: (_a) => {
            var { data, isLoading } = _a, rest = __rest(_a, ["data", "isLoading"]);
            return (Object.assign(Object.assign({}, rest), { isLoadingQueues: isLoading, searchQueueHits: (data === null || data === void 0 ? void 0 : data.data.results.hits) || [] }));
        },
    });
    // look up all the names of the queues for the facebook pages
    useEffect(() => {
        if (facebookApps && facebookApps.length > 0) {
            const queueIds = facebookApps.flatMap((app) => app.pages.map(({ queue_id }) => queue_id));
            // passing preferCacheValue to make the query use cache until new data arrives when switching tabs
            readSearchQueue({
                filter: {
                    facets: {
                        id: queueIds,
                    },
                },
            }, true);
        }
    }, [facebookApps, readSearchQueue]);
    /** Handles editing a facebook page */
    const handleEditFacebookPage = (facebookPage, searchQueue) => {
        dispatch(appActions.showModal({
            modal: {
                type: 'settings:editFacebookPage',
                arg: { facebookPage, searchQueue },
            },
        }));
    };
    /** Handles removing a facebook page. */
    const handleRemoveFacebookPage = (facebookPage) => __awaiter(void 0, void 0, void 0, function* () {
        const { isConfirmed } = yield dispatch(appActions.showModal({
            modal: {
                type: 'app:confirm',
                arg: {
                    title: `Remove "${facebookPage.name}"`,
                    description: 'Are you sure you want to remove your Facebook page from Dixa?',
                    confirm: {
                        label: 'Delete',
                    },
                },
            },
        }));
        if (isConfirmed) {
            try {
                yield deleteFacebookPage({
                    pageId: facebookPage.id,
                }).unwrap();
                notify({
                    title: 'Facebook page successfully removed',
                    time: 3000,
                }, NotificationType.Success);
            }
            catch (_a) {
                notify({
                    title: 'An error occured while removing the facebook page',
                }, NotificationType.Error);
            }
        }
    });
    const commonFacebookApps = useMemo(() => facebookApps
        .filter((app) => app.type === 'common')
        .map((app) => (Object.assign(Object.assign({}, app), { pages: app.pages.map((page) => (Object.assign(Object.assign({}, page), { searchQueue: searchQueueHits.find((hit) => hit.id === page.queue_id) }))) }))), [facebookApps, searchQueueHits]);
    const isLoading = isLoadingFacebook || isLoadingQueues;
    return (_jsxs(_Fragment, { children: [commonFacebookApps === null || commonFacebookApps === void 0 ? void 0 : commonFacebookApps.map((commonApp) => (_jsxs(_Fragment, { children: [_jsxs(FlexBox, Object.assign({ justifyContent: "space-between" }, { children: [_jsx(Text.Heading, Object.assign({ variant: "heading-1", withMarginBottom: true }, { children: commonApp.name })), _jsx(FacebookAuthenticator, Object.assign({ appId: commonApp.id, platform: "facebook" }, { children: "Add Facebook page" }))] })), commonApp.pages.length ? (_jsx(DataTable, { tableStatusProps: {
                            loading: isLoading,
                        }, columns: [
                            {
                                style: { width: '20%' },
                                label: 'Id',
                                selector: (item) => item.id,
                            },
                            {
                                style: { width: '35%' },
                                label: 'Name',
                                selector: (item) => item.name,
                            },
                            {
                                style: { width: '45%' },
                                label: 'Queue',
                                selector: (item) => {
                                    var _a;
                                    return (_jsxs(FlexBox, Object.assign({ alignItems: "center", justifyContent: "space-between" }, { children: [_jsx("div", { children: ((_a = item.searchQueue) === null || _a === void 0 ? void 0 : _a.name) || item.queue_id }), _jsxs("div", { children: [_jsx(Tooltip, Object.assign({ content: "Edit", dark: true }, { children: _jsx(IconButton, { variant: "tertiary", icon: "pen", onClick: () => handleEditFacebookPage(item, item.searchQueue) }) })), _jsx(Tooltip, Object.assign({ content: "Delete", dark: true }, { children: _jsx(IconButton, { variant: "tertiary", icon: "trash", onClick: () => handleRemoveFacebookPage(item) }) }))] })] })));
                                },
                            },
                        ], data: commonApp.pages })) : null] }))), _jsx(FacebookEditPageModal, {})] }));
};
